/**
 * The payoff, said out loud — ROADMAP 5.26.
 *
 * `explainPayoff` decides WHAT to say; this decides what it looks like.
 * The split exists because the sentences are worth testing and colour is
 * not, and because the same two lines belong on the portfolio screen next.
 *
 * COLOUR CARRIES THE KIND, not emphasis. Money made is green wherever it
 * appears, money lost is red, a price level is the foreground, the
 * instrument is the accent — so a glance finds the number being looked for
 * without reading the sentence around it.
 */

import { explainPayoff, type ExplainToken, type PayoffLeg } from '@apna/shared'
import { cn } from '@/lib/utils'
import { compactInr } from './quantity'

const TONE: Record<ExplainToken['kind'], string> = {
  text: 'text-muted-foreground',
  profit: 'font-semibold text-success',
  loss: 'font-semibold text-destructive',
  level: 'num font-semibold text-foreground',
  symbol: 'font-semibold text-primary',
}

export function PayoffExplain({
  legs,
  symbol,
  spot,
  className,
}: {
  legs: readonly PayoffLeg[]
  symbol: string
  spot: number | null
  className?: string
}) {
  const lines = explainPayoff(
    { legs, symbol, spot },
    {
      // `₹3.26 L` rather than `₹3,26,000`: the sentence is read at a
      // glance and six digits in the middle of it are not.
      money: (v) => `₹${compactInr(v)}`,
      level: (v) => Math.round(v).toLocaleString('en-IN'),
    },
  )
  if (lines.length === 0) return null

  return (
    <div
      className={cn('rounded-lg border border-border bg-muted/30 px-3 py-2', className)}
      data-testid="payoff-explain"
    >
      {lines.map((line, i) => (
        <p key={i} className="text-[12.5px] leading-[1.55]">
          {line.map((token, j) => (
            <span key={j} className={TONE[token.kind]}>
              {token.text}
            </span>
          ))}
        </p>
      ))}
    </div>
  )
}
